import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { btnPrimary } from "@/components/admin/ui";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, isAuthed, ready } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot

  useEffect(() => {
    if (ready && isAuthed) {
      api.adminMe().then(() => navigate("/admin", { replace: true })).catch(() => {});
    }
  }, [ready, isAuthed, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      await api.adminMe(); // verify admin
      navigate("/admin", { replace: true });
    } catch (err) {
      if (err?.response?.status === 403) toast.error("This account isn't a HomeRange admin.");
      else toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Sign in failed");
    } finally { setBusy(false); }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.forgotPassword({ email: email.trim().toLowerCase(), origin_url: window.location.origin });
      toast.success(res.message || "If an account exists, a reset link has been sent.");
      setMode("login");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F8F6]" data-testid="admin-login-page">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-[#1F2A22] text-[#F9F8F6] p-12 relative overflow-hidden">
        <div className="flex items-center gap-2">
          <span className="font-serif text-2xl">HomeRange</span>
          <span className="text-[10px] uppercase tracking-[0.2em] bg-[#FF6A00] text-white px-2 py-0.5">Admin</span>
        </div>
        <div>
          <h1 className="font-serif text-4xl leading-tight">The operating system<br />for HomeRange.</h1>
          <p className="text-white/60 mt-4 max-w-sm">Private, secure administration for owners and operators. Sales, revenue, businesses, partnerships and content — in one place.</p>
        </div>
        <div className="flex items-center gap-2 text-white/50 text-sm"><ShieldCheck size={16} /> Authorized personnel only</div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <span className="font-serif text-2xl text-[#1F2A22]">HomeRange</span>
            <span className="text-[10px] uppercase tracking-[0.2em] bg-[#FF6A00] text-white px-2 py-0.5">Admin</span>
          </div>
          <h2 className="font-serif text-3xl text-[#1F2A22]">{mode === "login" ? "Admin sign in" : "Reset password"}</h2>
          <p className="text-[#5C6B61] text-sm mt-1">{mode === "login" ? "Sign in with your HomeRange admin account." : "We'll email you a secure reset link."}</p>

          <form onSubmit={mode === "login" ? submit : sendReset} className="mt-8 space-y-4">
            <div>
              <label className="text-[11px] font-serif uppercase tracking-[0.15em] text-[#5C6B61]">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-[#E5E2DC] bg-white px-3 py-2.5 mt-1 text-sm outline-none focus:border-[#1F2A22]" data-testid="admin-login-email" />
            </div>
            {mode === "login" && (
              <div>
                <label className="text-[11px] font-serif uppercase tracking-[0.15em] text-[#5C6B61]">Password</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[#E5E2DC] bg-white px-3 py-2.5 mt-1 text-sm outline-none focus:border-[#1F2A22]" data-testid="admin-login-password" />
              </div>
            )}
            <button type="submit" disabled={busy} className={`${btnPrimary} w-full py-3`} data-testid="admin-login-submit">
              {busy ? <Loader2 size={16} className="animate-spin" /> : (mode === "login" ? "Sign in" : "Send reset link")}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            {mode === "login" ? (
              <button onClick={() => setMode("forgot")} className="text-[#8A5A44] hover:text-[#1F2A22]" data-testid="admin-forgot-link">Forgot password?</button>
            ) : (
              <button onClick={() => setMode("login")} className="text-[#8A5A44] hover:text-[#1F2A22] inline-flex items-center gap-1" data-testid="admin-back-login"><ArrowLeft size={13} /> Back to sign in</button>
            )}
            <button onClick={() => navigate("/")} className="text-[#5C6B61] hover:text-[#1F2A22]">Public site</button>
          </div>
        </div>
      </div>
    </div>
  );
}
